import { useState, useEffect, useMemo } from 'react';
import type { FileMetadata, UploadCategory } from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { FileUploader } from './FileUploader';

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All categories' },
  { value: 'evidences', label: 'Evidences' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'agreements', label: 'Agreements' },
  { value: 'certifications', label: 'Certifications' },
  { value: 'assessments', label: 'Assessments' },
  { value: 'general', label: 'General' },
];

interface StorageUsage {
  usedBytes: number;
  limitBytes: number;
}

export function FileList() {
  const { currentWorkspace } = useWorkspace();
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchFiles();
  }, [currentWorkspace]);

  async function fetchFiles() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const [filesResult, storageResult] = await Promise.all([
      apiClient.get<FileMetadata[]>(
        `/api/tenants/${currentWorkspace.tenantId}/files`
      ),
      apiClient.get<StorageUsage>(
        `/api/tenants/${currentWorkspace.tenantId}/files/usage`
      ),
    ]);

    if (filesResult.error) {
      setError(filesResult.error.message);
    } else if (filesResult.data) {
      setFiles(filesResult.data);
    }

    if (storageResult.data) {
      setStorageUsage(storageResult.data);
    }

    setLoading(false);
  }

  async function deleteFile(fileId: string) {
    if (!currentWorkspace) return;
    setDeletingId(fileId);

    const { error: apiError } = await apiClient.delete(
      `/api/tenants/${currentWorkspace.tenantId}/files/${fileId}`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    }
    setDeletingId(null);
  }

  async function downloadFile(fileId: string, filename: string) {
    if (!currentWorkspace) return;

    const { data, error: apiError } = await apiClient.get<{ downloadUrl: string }>(
      `/api/tenants/${currentWorkspace.tenantId}/files/${fileId}/download`
    );

    if (apiError) {
      setError(apiError.message);
      return;
    }

    if (data?.downloadUrl) {
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  function handleUploaded(file: FileMetadata) {
    setFiles((prev) => [file, ...prev]);
    setShowUpload(false);
  }

  const filteredFiles = useMemo(() => {
    if (!filterCategory) return files;
    return files.filter((f) => f.category === filterCategory);
  }, [files, filterCategory]);

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  const categoryBadgeVariant = (cat: UploadCategory) => {
    switch (cat) {
      case 'evidences': return 'success' as const;
      case 'architecture': return 'info' as const;
      case 'agreements': return 'brand' as const;
      case 'certifications': return 'warning' as const;
      case 'assessments': return 'danger' as const;
      case 'general': return 'default' as const;
      default: return 'default' as const;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Files</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-400 text-sm">
              {files.length} file{files.length !== 1 ? 's' : ''}
            </p>
            {storageUsage && (
              <p className="text-slate-500 text-sm">
                {formatBytes(storageUsage.usedBytes)} / {formatBytes(storageUsage.limitBytes)} used
              </p>
            )}
          </div>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          Upload File
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Storage usage bar */}
      {storageUsage && storageUsage.limitBytes > 0 && (
        <div className="bg-surface-mid rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-300">Storage Usage</span>
            <span className="text-sm text-slate-400">
              {Math.round((storageUsage.usedBytes / storageUsage.limitBytes) * 100)}%
            </span>
          </div>
          <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all"
              style={{
                width: `${Math.min(100, (storageUsage.usedBytes / storageUsage.limitBytes) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-surface-mid rounded-xl border border-border p-4">
        <Select
          label="Category"
          options={CATEGORY_OPTIONS}
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        />
      </div>

      {/* File list */}
      {filteredFiles.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">
              {files.length === 0
                ? 'No files uploaded yet.'
                : 'No files match the selected category.'}
            </p>
            {files.length === 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setShowUpload(true)}
              >
                Upload Your First File
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="bg-surface-mid rounded-xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-xs text-slate-500 uppercase tracking-wider">
            <div className="col-span-3">Filename</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1">Size</div>
            <div className="col-span-2">Uploaded By</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table rows */}
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center"
            >
              <div className="lg:col-span-3">
                <p className="text-sm font-medium text-white truncate" title={file.filename}>
                  {file.filename}
                </p>
                {file.linkedDeliverable && (
                  <p className="text-xs text-slate-500 truncate">
                    Linked to deliverable
                  </p>
                )}
              </div>
              <div className="lg:col-span-2">
                <Badge variant={categoryBadgeVariant(file.category)}>
                  {file.category}
                </Badge>
              </div>
              <div className="lg:col-span-1">
                <p className="text-sm text-slate-400">{formatBytes(file.sizeBytes)}</p>
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-slate-400 truncate">{file.uploadedBy}</p>
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-slate-500">{formatDate(file.uploadedAt)}</p>
              </div>
              <div className="lg:col-span-2 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadFile(file.id, file.filename)}
                >
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={deletingId === file.id}
                  onClick={() => deleteFile(file.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      <Modal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        title="Upload File"
        size="md"
      >
        <FileUploader onUploaded={handleUploaded} />
      </Modal>
    </div>
  );
}
